/**
 * Shared content for industries — used by /industries (list) and
 * /industries/:slug (detail) pages. Keeping the source of truth here so
 * every industry has consistent narrative + SEO surface.
 */

export const INDUSTRIES = [
    {
        slug: "healthcare",
        name: "Healthcare & Life Sciences",
        short: "AI inside regulated healthcare and life-sciences workflows.",
        tagline: "Modernise clinical and operational workflows with AI you can trust.",
        intro:
            "Perceptiqx helps healthcare providers, payers and life-sciences organisations adopt AI inside the systems they already run — without compromising on compliance, auditability or clinical safety.",
        challenges: [
            "Fragmented patient, clinical and operational data across EHR, RCM and back-office systems.",
            "Rising operational, coding and compliance overhead — with limited bandwidth on the floor.",
            "Slow adoption of AI inside regulated workflows where every output must be defensible.",
        ],
        useCases: [
            {
                title: "Clinical document intelligence",
                copy: "Structured extraction from clinical notes, discharge summaries and faxed referrals — with confidence scoring and human review.",
            },
            {
                title: "AI copilots for outreach & triage",
                copy: "Patient-facing copilots for scheduling, reminders and triage — grounded in your protocols and policies.",
            },
            {
                title: "Predictive risk & readmission",
                copy: "Explainable risk models for readmission, deterioration and cost — surfaced inside clinician dashboards.",
            },
            {
                title: "Prior-auth automation",
                copy: "Agentic workflows that draft, evaluate and submit prior-authorisation packages with full audit trails.",
            },
        ],
        talent:
            "Real-world-evidence engineers, FHIR specialists, clinical NLP scientists, HIPAA-aware cloud and platform engineers, and product designers experienced with clinician workflows.",
        caseStudy: {
            title: "Speciality clinic group — 62% faster prior-auth turnaround",
            challenge:
                "A multi-state speciality clinic group was losing weeks per case to manual prior-authorisation packaging across payers, with inconsistent denial rates and limited audit trails.",
            approach:
                "We deployed a DocuIQX-based document intake layer paired with AgentIQX agents that extracted clinical evidence, drafted payer-specific packages and routed exceptions for human review.",
            outcome:
                "Prior-authorisation turnaround dropped from 11 days to 4. Denial rates declined 23% within the first quarter. Compliance reviewers retained full visibility through structured audit logs.",
            metrics: [
                { label: "Turnaround", value: "−62%" },
                { label: "Denials", value: "−23%" },
                { label: "Hours saved / month", value: "1,800+" },
            ],
        },
        kpis: [
            { value: "48h", label: "Talent shortlists" },
            { value: "30+", label: "Healthcare AI deployments" },
            { value: "HIPAA", label: "Aware programs" },
        ],
        related: ["bfsi", "tech"],
    },
    {
        slug: "bfsi",
        name: "Banking & Financial Services",
        short: "AI for risk, compliance and customer experience in BFSI.",
        tagline: "Bring AI into BFSI with the rigour your regulators expect.",
        intro:
            "Perceptiqx partners with banks, NBFCs, insurers and capital markets firms to deploy AI inside risk, compliance and customer-experience workflows — engineered for governance and explainability.",
        challenges: [
            "Legacy core systems coexisting with modern digital channels and high transaction volumes.",
            "Tightening regulatory and audit expectations across model risk, AML and consumer protection.",
            "AI deployment in environments with very low tolerance for opaque or unstable behaviour.",
        ],
        useCases: [
            {
                title: "KYC & onboarding intelligence",
                copy: "AI-assisted document review, sanctions screening and customer-risk scoring across onboarding journeys.",
            },
            {
                title: "Fraud & anomaly detection",
                copy: "Real-time transaction monitoring with explainable scoring — tuned to your fraud taxonomy.",
            },
            {
                title: "Document intelligence for credit & claims",
                copy: "Loan files, claims packages and contracts — extracted, classified and routed with auditability.",
            },
            {
                title: "Compliance copilots",
                copy: "AI copilots that draft, review and summarise policy documents, RFIs and regulator submissions.",
            },
        ],
        talent:
            "Risk and quant engineers, BFSI domain analysts, security-cleared cloud and data engineers, ML platform specialists with model-risk experience.",
        caseStudy: {
            title: "Regional bank — credit document review reduced from days to hours",
            challenge:
                "A regional bank's commercial lending team handled hundreds of loan files per month, each requiring manual extraction across financial statements, KYC docs and covenant checklists.",
            approach:
                "We delivered a DocuIQX pipeline integrated with their core banking workflow, plus a recruiter-led talent pod that staffed the program with credit-domain analysts and ML engineers.",
            outcome:
                "Document review cycle compressed from 48 hours to under 4 hours. Reviewer productivity rose 3.2× with full extraction audit trails accepted by internal audit.",
            metrics: [
                { label: "Cycle time", value: "−92%" },
                { label: "Reviewer productivity", value: "3.2×" },
                { label: "Audit acceptance", value: "100%" },
            ],
        },
        kpis: [
            { value: "ISO-27001", label: "Aligned engagements" },
            { value: "RBI / SEBI", label: "Aware delivery" },
            { value: "24h", label: "Risk pod stand-up" },
        ],
        related: ["healthcare", "tech"],
    },
    {
        slug: "retail",
        name: "Retail & E-commerce",
        short: "AI for personalisation, search and customer operations.",
        tagline: "Personalise, optimise and scale across every customer touchpoint.",
        intro:
            "Perceptiqx works with retailers, marketplaces and direct-to-consumer brands to embed AI inside search, personalisation, supply and service — driving measurable revenue and margin impact.",
        challenges: [
            "Personalisation expectations across very large catalogues and channels.",
            "Operational pressure across promotions, pricing and inventory cycles.",
            "Customer-service quality under cost pressure as volumes grow.",
        ],
        useCases: [
            {
                title: "AI search & discovery",
                copy: "Semantic search, query understanding and personalised ranking with measurable conversion lift.",
            },
            {
                title: "Conversational commerce",
                copy: "Customer copilots that answer, recommend and complete transactions across channels.",
            },
            {
                title: "Catalog enrichment & moderation",
                copy: "AI to enrich, tag and moderate catalog content at scale — with human-in-the-loop review.",
            },
            {
                title: "Demand & price intelligence",
                copy: "Forecasting and dynamic pricing models tuned to your category mix and customer segments.",
            },
        ],
        talent:
            "Search and recsys engineers, growth analysts, conversational designers, CMS and integration specialists.",
        caseStudy: {
            title: "Multi-brand marketplace — 18% lift in search conversion",
            challenge:
                "A multi-category online marketplace was losing conversion on long-tail queries due to a keyword-based search stack and inconsistent attribute coverage.",
            approach:
                "We rolled out a hybrid AI search layer with semantic retrieval, query rewriting and personalised ranking — alongside DocuIQX-based catalog enrichment.",
            outcome:
                "Search conversion lifted 18% on long-tail queries. Zero-result rates dropped by 41%. Catalog completeness rose to 96% across the top 500 attributes.",
            metrics: [
                { label: "Search conversion", value: "+18%" },
                { label: "Zero-result rate", value: "−41%" },
                { label: "Catalog completeness", value: "96%" },
            ],
        },
        kpis: [
            { value: "10M+", label: "Catalog rows processed" },
            { value: "+18%", label: "Search conversion" },
            { value: "24/7", label: "Service copilots" },
        ],
        related: ["logistics", "tech"],
    },
    {
        slug: "logistics",
        name: "Logistics & Supply Chain",
        short: "AI for routing, exceptions and document-heavy supply chain ops.",
        tagline: "Bring AI to the edges of your supply chain — where exceptions live.",
        intro:
            "Perceptiqx works with 3PLs, freight forwarders, ports and supply-chain teams to bring AI into the operational edges — where exceptions, documents and decisions accumulate.",
        challenges: [
            "Complex routing, scheduling and exception handling across modes and partners.",
            "Limited visibility across multi-party operations and legacy EDI infrastructure.",
            "Manual document processing slows everything downstream of the dock.",
        ],
        useCases: [
            {
                title: "Route & load optimisation",
                copy: "Optimisation models for routing, slotting and load building — integrated with TMS/WMS.",
            },
            {
                title: "Document intake & matching",
                copy: "Automated parsing and matching across BOLs, invoices, customs and proof-of-delivery documents.",
            },
            {
                title: "Exception management agents",
                copy: "Agents that detect, classify and route exceptions with human approval thresholds.",
            },
            {
                title: "Predictive ETA & visibility",
                copy: "Predictive ETAs and risk scoring across shipments — surfaced inside operator dashboards.",
            },
        ],
        talent:
            "Optimisation specialists, geospatial engineers, EDI/integration developers, supply-chain analysts and platform engineers.",
        caseStudy: {
            title: "Global 3PL — 5× faster freight document intake",
            challenge:
                "A global 3PL was processing tens of thousands of freight documents monthly, with manual data entry creating downstream billing and customs delays.",
            approach:
                "We deployed DocuIQX for multi-format intake, paired with AgentIQX agents for matching and exception routing — all wired into their TMS.",
            outcome:
                "Document intake throughput rose 5×. Customs filing delays fell 70%. Billing exception backlog cleared within 6 weeks.",
            metrics: [
                { label: "Throughput", value: "5×" },
                { label: "Customs delays", value: "−70%" },
                { label: "Backlog cleared", value: "6 wks" },
            ],
        },
        kpis: [
            { value: "5×", label: "Doc intake throughput" },
            { value: "−70%", label: "Customs delays" },
            { value: "Multi-modal", label: "Coverage" },
        ],
        related: ["manufacturing", "retail"],
    },
    {
        slug: "manufacturing",
        name: "Manufacturing",
        short: "Bridge OT and modern cloud analytics with AI you can trust on the floor.",
        tagline: "Bring AI to the plant floor — without disrupting OT.",
        intro:
            "Perceptiqx helps discrete and process manufacturers bridge OT systems with modern cloud and AI capabilities — improving quality, uptime and operator knowledge transfer.",
        challenges: [
            "Bridging OT, MES and cloud analytics safely and securely.",
            "Quality, downtime and energy efficiency at industrial scale.",
            "Operator knowledge transfer as senior teams retire.",
        ],
        useCases: [
            {
                title: "Predictive maintenance",
                copy: "Models trained on telemetry to predict failures and surface guided maintenance actions.",
            },
            {
                title: "Quality vision & analytics",
                copy: "Computer-vision and statistical models for in-line quality detection and root-cause analysis.",
            },
            {
                title: "Operator knowledge copilots",
                copy: "AI copilots grounded in SOPs, manuals and maintenance history — preserving institutional knowledge.",
            },
            {
                title: "Procurement document intelligence",
                copy: "Vendor and procurement document automation across RFx, contracts and compliance documents.",
            },
        ],
        talent:
            "IIoT engineers, plant analytics specialists, cloud and integration engineers, reliability and SRE engineers.",
        caseStudy: {
            title: "Process manufacturer — predictive maintenance saved 11% of unplanned downtime",
            challenge:
                "A process manufacturer was losing meaningful production hours to unplanned downtime on critical pump and motor assets, with limited telemetry-driven prediction in place.",
            approach:
                "We delivered a predictive maintenance program with edge-aware data pipelines, model evaluation and an operator-facing AgentIQX assistant tied to maintenance SOPs.",
            outcome:
                "Unplanned downtime fell 11% within the first year. Mean time between failures rose 28%. Operator onboarding compressed from 9 weeks to 4.",
            metrics: [
                { label: "Unplanned downtime", value: "−11%" },
                { label: "MTBF", value: "+28%" },
                { label: "Onboarding time", value: "−55%" },
            ],
        },
        kpis: [
            { value: "OT-safe", label: "Deployment patterns" },
            { value: "Edge + Cloud", label: "Architectures" },
            { value: "−11%", label: "Unplanned downtime" },
        ],
        related: ["logistics", "tech"],
    },
    {
        slug: "tech",
        name: "Technology & SaaS",
        short: "AI-native product features and the engineering capacity to ship them.",
        tagline: "Ship AI-native features without breaking the rest of your roadmap.",
        intro:
            "Perceptiqx works with product companies and SaaS leaders to ship AI-native features inside existing products — and to scale the engineering capacity it takes to keep up.",
        challenges: [
            "Pressure to ship AI features without breaking the rest of the roadmap.",
            "Scaling engineering and ML platform capacity for new product lines.",
            "Operating cost, reliability and evaluation of AI systems in production.",
        ],
        useCases: [
            {
                title: "Native AI features",
                copy: "Embed retrieval, agents and copilots inside existing SaaS surfaces without breaking the data model.",
            },
            {
                title: "Customer-facing copilots",
                copy: "Copilots inside customer success, support and onboarding — with measurable activation lifts.",
            },
            {
                title: "Evaluation infrastructure",
                copy: "Reusable evaluation harnesses for prompts, retrieval and agentic workflows — visible to product.",
            },
            {
                title: "ML platform & MLOps",
                copy: "Hardened pipelines for training, serving and monitoring — designed around your existing stack.",
            },
        ],
        talent:
            "Senior product engineers, ML platform engineers, applied scientists, DevOps and reliability specialists.",
        caseStudy: {
            title: "Series B SaaS — AI copilot launched in 9 weeks with 34% activation lift",
            challenge:
                "A Series B SaaS company needed to ship an AI copilot inside their core product within a quarter, without diverting their core product engineers or expanding headcount permanently.",
            approach:
                "We assembled a senior AI pod (ML, backend, frontend, design) embedded with their product team — building on top of their existing data model and shipping behind feature flags.",
            outcome:
                "The copilot launched in 9 weeks. 34% of trial users activated the copilot in their first session. Day-30 retention rose 18% on cohorts using the copilot.",
            metrics: [
                { label: "Time to ship", value: "9 wks" },
                { label: "Trial activation", value: "+34%" },
                { label: "D30 retention", value: "+18%" },
            ],
        },
        kpis: [
            { value: "9 wks", label: "Avg copilot ship time" },
            { value: "+34%", label: "Activation lift" },
            { value: "Senior pods", label: "Default" },
        ],
        related: ["healthcare", "bfsi"],
    },
];

export const getIndustry = (slug) => INDUSTRIES.find((i) => i.slug === slug);
